import { createTheme, Theme } from '@mui/material/styles';
// noinspection ES6UnusedImports
import {} from '@mui/x-data-grid/themeAugmentation';
import OpenSansRegular from '@assets/fonts/OpenSans/OpenSans-Regular.ttf';
import { darken, responsiveFontSizes } from '@mui/material';

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    hzMenuButton: true;
    filterTabButton: true;
  }
}

declare module '@mui/material/styles' {
  interface Palette {
    accent?: Palette['primary'];
    white?: Palette['primary'];
    chart?: Palette['primary'];
    foggyGrey?: Palette['primary'];
    border?: Palette['primary'];
    offwhite?: Palette['primary'];
    infopaper?: Palette['primary'];
  }

  interface PaletteOptions {
    accent?: PaletteOptions['primary'];
    white?: PaletteOptions['primary'];
    chart?: PaletteOptions['primary'];
    foggyGrey?: PaletteOptions['primary'];
    border?: PaletteOptions['primary'];
    offwhite?: PaletteOptions['primary'];
    infopaper?: PaletteOptions['primary'];
  }

  interface Theme {
    customValues: {
      maxWidth: string;
      minHeightSignedIn: string;
      minHeightSignedOut: string;
      flexCenter: Record<string, string>;
      sectionPaddingX: string;
      sectionPaddingY: string;
      itemSpace: string;
      containerPadding: string;
      containerSpace: string;
      borderRadius: string;
      fontSizes: {
        body32: string;
        body24: string;
        body20: string;
        body18: string;
        body16: string;
        body14: string;
        body12: string;
      };
      layoutStyles: {
        pageRoot: Record<string, string | number>;
        pageRootStatusContainer: Record<string, string>;
        stepper: Record<string, string | number>;
      };
    };
  }

  interface ThemeOptions {
    customValues?: {
      maxWidth?: string;
      minHeightSignedIn?: string;
      minHeightSignedOut?: string;
      flexCenter: Record<string, string>;
      sectionPaddingX: string;
      sectionPaddingY: string;
      itemSpace: string;
      containerPadding: string;
      containerSpace: string;
      borderRadius: string;
      fontSizes: {
        body32: string;
        body24: string;
        body20: string;
        body18: string;
        body16: string;
        body14: string;
        body12: string;
      };
      layoutStyles: {
        pageRoot: Record<string, string | number>;
        pageRootStatusContainer: Record<string, string>;
        stepper: Record<string, string | number>;
      };
    };
  }
}

let HzTheme: Theme = createTheme({
  palette: {
    primary: {
      main: '#428bca',
      dark: '#326499',
    },
    secondary: {
      main: '#222222',
      dark: '#151515',
      light: '#8d8d8d',
    },
    info: {
      main: '#22AAEF',
      dark: '#428BCA',
      light: '#d7ecf6',
    },
    success: {
      main: '#1EC055',
      light: '#DFF0D8',
    },
    warning: {
      main: '#FF9822',
    },
    error: {
      main: '#FD3924',
      light: '#f2dede',
    },
    chart: {
      main: '#a2c6d9',
      light: '#74C5EC',
    },
    accent: {
      main: '#ffcc00',
    },
    white: {
      main: '#fafafa',
      light: '#ffffff',
    },
    foggyGrey: {
      main: '#999',
    },
    border: {
      main: '#d1d1d1',
    },
    offwhite: {
      main: '#EFEBE0',
      dark: '#E5E2D7',
    },
    infopaper: {
      main: '#ffe',
    },
  },
  customValues: {
    maxWidth: '1200px',
    minHeightSignedIn: 'calc(100vh - 30.5rem)',
    minHeightSignedOut: 'calc(100vh - 9.5rem)',
    sectionPaddingX: '1.5rem',
    sectionPaddingY: '3.5rem',
    itemSpace: '1rem',
    containerPadding: '1.5rem',
    containerSpace: '3rem',
    borderRadius: '4px',
    flexCenter: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    fontSizes: {
      body32: '2rem',
      body24: '1.5rem',
      body20: '1.25rem',
      body18: '1.125rem',
      body16: '1rem',
      body14: '0.875rem',
      body12: '0.750rem',
    },
    layoutStyles: {
      pageRoot: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        width: '100%',
        padding: '3.5rem 1.5rem',
        minHeight: 'calc(100vh - 30.5rem)',
      },
      pageRootStatusContainer: {
        minHeight: 'calc(100vh - 30.5rem)',
        py: '3.5rem',
        px: '1.5rem',
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
      },
      stepper: {
        padding: 0,
        width: '100%',
        maxWidth: '1600px',
        gap: '1rem',
      },
    },
  },
  typography: {
    fontFamily: 'Open Sans',
    h1: {
      fontSize: '2rem',
    },
    h2: {
      fontSize: '1.8rem',
    },
    h3: {
      fontSize: '1.6rem',
    },
  },
  components: {
    MuiTypography: {},
    MuiAlert: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.severity === 'error' &&
            ownerState.variant === 'standard' && {
              backgroundColor: '#FCEBE9',
              color: '#751A0C',
            }),
          ...(ownerState.severity === 'warning' &&
            ownerState.variant === 'standard' && {
              backgroundColor: '#FFF6E5',
              color: '#93541B',
            }),
          ...(ownerState.severity === 'info' &&
            ownerState.variant === 'standard' && {
              backgroundColor: '#E5F3FB',
              color: '#004B6F',
            }),
          ...(ownerState.severity === 'success' &&
            ownerState.variant === 'standard' && {
              backgroundColor: '#E6F9ED',
              color: '#006730',
            }),
        }),
        icon: ({ ownerState }) => ({
          ...(ownerState.severity === 'error' &&
            ownerState.variant === 'standard' && {
              color: 'grey',
            }),
          ...(ownerState.severity === 'warning' &&
            ownerState.variant === 'standard' && {
              color: '#FFAD00',
            }),
          ...(ownerState.severity === 'info' &&
            ownerState.variant === 'standard' && {
              color: '#008EDB',
            }),
          ...(ownerState.severity === 'success' &&
            ownerState.variant === 'standard' && {
              color: '#0EC54F',
            }),
        }),
      },
    },
    MuiCssBaseline: {
      styleOverrides: `
        @font-face {
          font-family: 'Open Sans';
          font-style: normal;
          font-display: swap;
          font-weight: normal;
          src: local('Open Sans'), local('OpenSans-Regular'), url(${OpenSansRegular}) format('truetype');
      `,
    },
    MuiDataGrid: {
      styleOverrides: {
        columnHeaderTitle: {
          fontWeight: 'bold',
          textTransform: 'uppercase',
        },
        cell: {
          display: 'flex',
          alignItems: 'center',
          '& .MuiDataGrid-cellContent': {
            wordBreak: 'break-all',
            whiteSpace: 'initial',
            textTransform: 'uppercase',
          },
        },
      },
    },
    MuiButton: {
      variants: [
        {
          props: { variant: 'hzMenuButton' },
          style: {
            display: 'flex',
            color: 'white',
            fontWeight: '300',
            fontSize: '0.9em',
            borderRight: '1px solid #777',
            borderRadius: '0',
            padding: '0 2em',
            '&:hover': {
              backgroundColor: '#333',
              color: '#ffcc00',
            },
          },
        },
        {
          props: { variant: 'filterTabButton' },
          style: ({ theme }) => ({
            background: theme.palette.accent.main,
            color: theme.palette.secondary.dark,
            border: 'none',
            fontWeight: 900,
            '&:hover': {
              background: darken(theme.palette.accent.main, 0.05),
              boxShadow: theme.shadows[4],
              border: 'none',
            },
          }),
        },
      ],
    },
    MuiTextField: {
      styleOverrides: {
        root: ({ theme }) => ({
          marginTop: '8px',
          marginBottom: '8px',
          width: '85%',
          maxWidth: '48ch',
          '& .MuiInputBase-root': {
            backgroundColor: theme.palette.white.light,
          },
        }),
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          marginTop: '8px',
          marginBottom: '8px',
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: '#E0E0E0',
          width: '90%',
          maxWidth: '100ch',
        },
      },
    },
  },
});

HzTheme = responsiveFontSizes(HzTheme);

export default HzTheme;
